import { debounceTime, Observable, Subject, Subscription, switchMap } from 'rxjs';

/**
 * A class that runs a task with a debounce time
 */
export default class ThrottledTaskRunner<T = unknown> {
  private readonly trigger$: Subject<T>;

  private readonly throttledTask$: Observable<void>;

  private subscription: Subscription;

  /**
   * Create a new ThrottledTaskRunner
   * @param task The task to run
   * @param debounce The debounce time in milliseconds
   */
  constructor(task: (args?: T) => PromiseLike<void> | void, debounce = 200) {
    this.trigger$ = new Subject();
    this.throttledTask$ = this.trigger$.pipe(
      debounceTime(debounce),
      switchMap(async (args) => task(args)),
    );
  }

  /**
   * Run the task with the provided arguments
   * @param args The arguments to pass to the task
   */
  public run(args?: T) {
    if (this.subscription == null) {
      this.subscription = this.throttledTask$.subscribe();
    }
    this.trigger$.next(args);
  }

  /**
   * Destroy the task runner
   */
  public destroy() {
    this.subscription?.unsubscribe();
  }
}
